import type { PickupComparisonField } from './pickupReconciliationPolicy';
import {
  PICKUP_LOAN_CENTER_LABEL,
  PICKUP_OPTIMAL_BLUE_LABEL
} from './pickupVerificationPresentation';

export type PickupComparisonSide = 'encompass' | 'ob';

const AMOUNT_FIELDS = new Set<PickupComparisonField>([
  'base_loan_amount',
  'estimated_value',
  'appraised_value',
  'funding_amount',
  'mip_paid_in_cash'
]);

const PRICE_FIELDS = new Set<PickupComparisonField>([
  'locked_price'
]);

const amountFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});

function numericValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().replace(/,/g, '');
  if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatPickupComparisonValue(
  field: PickupComparisonField,
  value: unknown,
  side: PickupComparisonSide
): string {
  if (value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0)) {
    return side === 'ob'
      ? `Not in ${PICKUP_OPTIMAL_BLUE_LABEL} record`
      : `Not available in ${PICKUP_LOAN_CENTER_LABEL}`;
  }
  if (Array.isArray(value)) {
    return value.map(String).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  const numeric = numericValue(value);
  if (numeric !== null && PRICE_FIELDS.has(field)) {
    return numeric.toFixed(3);
  }
  if (numeric !== null && AMOUNT_FIELDS.has(field)) {
    return amountFormatter.format(numeric);
  }
  return String(value);
}
